import { FC } from "react";
import { useLocation } from "react-router-dom";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { trackOnboarding as track } from "~/events";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { QuestionPageContainer } from "~/OnboardingApp/components";
import { useMetaByPathConfig } from "~/OnboardingApp/features/Nav/hooks";
import { isFinalQuestion } from "~/OnboardingApp/features/Nav/utils";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { conditionalPhone } from "~/utils/validations";
import { PhoneForm, DuplicatePhone } from "./components";
import { formatPhone } from "./utils";

export type Values = {
  phone: string;
  international: boolean;
};

const validationSchema = Yup.object({
  phone: conditionalPhone,
  international: Yup.boolean(),
});

export const PhonePage: FC = () => {
  const { international, phone } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();
  const { pathname } = useLocation();
  const { metaByPath, questionnaireOrder } = useMetaByPathConfig();
  const user = useCurrentUser();
  const isNotOnboarded = !user?.isOnboarded;

  const initialValues: Values = {
    phone: phone ? formatPhone({ value: phone, oldValue: "" }) : "",
    international: international || false,
  };

  // The partner flow ends here, so this submit is the one that completes
  // onboarding and has to fire the conversion event. In the default flow the
  // phone page sits mid-wizard and HowDidYouHearPage fires it instead.
  const handleSubmit = (
    values: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    const completesOnboarding =
      isNotOnboarded &&
      isFinalQuestion({
        attemptingRoute: pathname,
        metaByPath,
        questionnaireOrder,
      });

    completesOnboarding && track.submit();
    onSubmit(values, formikHelpers);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
  });

  if (formik.errors.phone == "Phone number not available.") {
    return (
      <QuestionPageContainer>
        <DuplicatePhone />
      </QuestionPageContainer>
    );
  }

  return (
    <QuestionPageContainer>
      <PhoneForm formik={formik} />
    </QuestionPageContainer>
  );
};
